import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { checkErrorAPICall } from '../../../../helpers/checkErrorAPICall'
import { actionTypes } from '../../../../modules/auth/redux/PagesRedux'
import { useFreightSnap } from '../../../../helpers/FreightSnap/useFreightSnap'
import { freightSnapScan, freightSnapErrorMessage } from '../../../../helpers/FreightSnap/freightSnapService'
import { FreightSnapScan } from '../../../../helpers/FreightSnap/types'
import { TypeOptions } from '../../../../data/TypeOptions'
import DimensionerModal from './DimensionerModal'
import FreightSnapControl from './FreightSnapControl'

interface Props {
    index: number
    setDataFormik: (path: string, value: any) => void
    BOLNumber?: string
    currentFreightSnapId?: string | number | null
    currentQty?: string | number
    currentWeight?: string | number
    currentL?: string | number
    currentW?: string | number
    currentH?: string | number
    freightInfoUOM?: {value?: string}
    hasRate?: boolean
    disabled?: boolean
}

const round = (n: number) => Math.round((n || 0) * 100) / 100

const FreightSnapAction = ({index, setDataFormik, BOLNumber, currentFreightSnapId, currentQty, currentWeight, currentL, currentW, currentH, freightInfoUOM, hasRate, disabled}: Props) => {
    const dispatch = useDispatch()
    const {enabled, defaultDimensionerId, locationId, dimensioners} = useFreightSnap()
    const [openModal, setOpenModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    const scanned = !!currentFreightSnapId

    const storedScan: FreightSnapScan | undefined = useMemo(() => (
        scanned ? {
            id: String(currentFreightSnapId),
            weight: Number(currentWeight) || 0,
            length: Number(currentL) || 0,
            width: Number(currentW) || 0,
            height: Number(currentH) || 0,
            dimensionUom: 'in',
            weightUom: 'lbs',
            images: [],
            imageCount: 0,
        } : undefined
    ), [scanned, currentFreightSnapId, currentWeight, currentL, currentW, currentH])

    if(!enabled) return null

    const applyScan = (scan: FreightSnapScan, usedDimensionerId?: string | number) => {
        const isNewScan = String(scan.id) !== String(currentFreightSnapId || '')
        setError(false)
        const toDisplay = (usValue: number, kind: 'weight' | 'dim') => {
            if(!freightInfoUOM || freightInfoUOM.value === 'US') return usValue
            return kind === 'weight' ? usValue * 0.45359237 : usValue * 2.54
        }
        setDataFormik(`freightInfo[${index}].weight`, round(toDisplay(scan.weight, 'weight')))
        setDataFormik(`freightInfo[${index}].weightus`, round(scan.weight))
        setDataFormik(`freightInfo[${index}].L`, round(toDisplay(scan.length, 'dim')))
        setDataFormik(`freightInfo[${index}].W`, round(toDisplay(scan.width, 'dim')))
        setDataFormik(`freightInfo[${index}].H`, round(toDisplay(scan.height, 'dim')))
        setDataFormik(`freightInfo[${index}].Lus`, round(scan.length))
        setDataFormik(`freightInfo[${index}].Wus`, round(scan.width))
        setDataFormik(`freightInfo[${index}].Hus`, round(scan.height))
        if(currentQty === '' || currentQty === undefined || currentQty === null){
            setDataFormik(`freightInfo[${index}].qty`, 1)
        }
        const usedDim = dimensioners.find((d) => String(d.dimensionerId) === String(usedDimensionerId))
        const pkgRaw: any = usedDim?.defaultPackageType
        const pkgCode = pkgRaw && typeof pkgRaw === 'object' ? pkgRaw.value : pkgRaw
        if(pkgCode){
            const option = TypeOptions.find((t: any) => t.value === pkgCode)
            if(option){
                setDataFormik(`freightInfo[${index}].dimType`, option)
            }
        }
        setDataFormik(`freightInfo[${index}].freightsnapId`, scan.id)
        if(hasRate){
            setDataFormik('carrierInformation', {})
            setDataFormik('url', '')
            setDataFormik('quoteNumber', '')
            setDataFormik('vendorId', '')
            setDataFormik('vendorSCAC', '')
        }
        if(isNewScan){
            dispatch({type: actionTypes.SetAxiosSuccess, payload: 'Dimensions captured from FreightSnap.'})
        }
    }

    const autoScan = (dimensionerId: string | number) => {
        setError(false)
        setLoading(true)
        freightSnapScan({locationId, dimensionerId, BOLNumber})
            .then((scan) => {
                applyScan(scan, dimensionerId)
                setLoading(false)
            })
            .catch((err) => {
                setLoading(false)
                setError(true)
                const friendly = freightSnapErrorMessage(err)
                if(friendly){
                    dispatch({type: actionTypes.SetAxiosErrors, payload: friendly})
                }else{
                    checkErrorAPICall(err, dispatch, 'FreightSnap')
                }
            })
    }

    const handleScanClick = () => {
        if(disabled || loading) return
        const auto = defaultDimensionerId ?? (dimensioners.length === 1 ? dimensioners[0].dimensionerId : undefined)
        if(auto !== undefined && auto !== null && auto !== ''){
            autoScan(auto)
        }else{
            setOpenModal(true)
        }
    }

    return (
        <div className="d-flex align-items-center mt-4">
            <FreightSnapControl
                scanned={scanned}
                loading={loading}
                error={error}
                disabled={disabled}
                onScan={handleScanClick}
                onDetails={() => setOpenModal(true)}
            />

            <DimensionerModal
                open={openModal}
                setOpen={setOpenModal}
                dimensioners={dimensioners}
                defaultDimensionerId={defaultDimensionerId}
                locationId={locationId}
                BOLNumber={BOLNumber}
                initialScan={storedScan}
                freightInfoUOM={freightInfoUOM}
                onApply={applyScan}
            />
        </div>
    )
}

export default FreightSnapAction
